/**
 * The muse intake's return from the payment page and from the sign-in. The address-bar rules are core's
 * (`@core/billing/checkoutReturn`); this file is Ovid's words and her name. Two checkouts return here, each to the
 * intake's reopen address: `/checkout/muse` (`session_id`, or `checkout=` when nothing was paid) and `/checkout/plan`
 * (`session_id` with `plan=success`, or `plan=` alone when nothing was paid). `/checkout/muse` declines to sell
 * twice (`paid`: a muse purchase is already open, Start proceeds on it) and `/checkout/plan` likewise (`active`: a
 * plan is already in force); a model being trained does not stop a purchase.
 */
import {
  type CheckoutReturn as CoreCheckoutReturn,
  readCheckoutReturn as coreReadCheckoutReturn,
  withParam,
} from '@core/billing/checkoutReturn'
import {OWN_MUSE_NAME_MAX} from '@product/ovid/muses/museKey'

export type CheckoutOutcome = 'cancelled' | 'failed' | 'paid' | 'active'

/** The one line a return from Checkout without a new payment leaves on the intake. */
export const CHECKOUT_NOTICE: Record<CheckoutOutcome, string> = {
  cancelled: 'Payment cancelled. Your photos are still here.',
  failed: 'We could not open the payment page. Please try again.',
  paid: 'Payment received. Your photos are still here.',
  active: 'Your plan is already active. Your photos are still here.',
}

export type CheckoutReturn = CoreCheckoutReturn<CheckoutOutcome>

/** `/checkout/plan`'s own parameter, and what each of its values means for the intake. */
export const PLAN_RETURN_PARAM = 'plan'
const PLAN_OUTCOME: Record<string, CheckoutOutcome> = {cancelled: 'cancelled', failed: 'failed', active: 'active'}

/** Whether a return without a new payment leaves Start free to run: something already paid for it. */
export function startsOnReturn(outcome: CheckoutOutcome | null): boolean {
  return outcome === 'paid' || outcome === 'active'
}

/** The checkout a paid return (`session_id`) came from: the plan's carries `plan=success` beside it. */
export type PaidReturnKind = 'plan' | 'muse'

export function paidReturnKind(search: string): PaidReturnKind {
  return new URLSearchParams(search).get(PLAN_RETURN_PARAM) === 'success' ? 'plan' : 'muse'
}

/**
 * What a 402 from Start leaves owed, or null when Start should run again on the session it returned with. Without a
 * paid return: what the refusal names. On a return from the checkout the refusal names, the payment is not confirmed
 * yet: nothing new is owed. On a return from the other one — the plan paid, then the extra muse asked because a muse
 * of hers already holds the plan's slot — the session is spent and what the refusal names is owed.
 */
export function owedAfterRefusal(
  refusal: 'plan-required' | 'payment-required',
  returnedFrom: PaidReturnKind | null,
): 'plan' | 'muse' | null {
  const named = refusal === 'plan-required' ? 'plan' : 'muse'
  return named === returnedFrom ? null : named
}

export function readCheckoutReturn(search: string): CheckoutReturn {
  const muse = coreReadCheckoutReturn(search, CHECKOUT_NOTICE)
  if (muse.sessionId || muse.outcome) return muse
  const plan = new URLSearchParams(search).get(PLAN_RETURN_PARAM)
  const outcome = plan && Object.prototype.hasOwnProperty.call(PLAN_OUTCOME, plan) ? PLAN_OUTCOME[plan] : null
  return outcome ? {...muse, outcome, notice: CHECKOUT_NOTICE[outcome], cancelled: outcome === 'cancelled'} : muse
}

/** localStorage key prefix of the once-per-payment `purchase` memory. */
export const PURCHASE_TRACKED_PREFIX = 'ovid_purchase_tracked'

/**
 * Her name rides the addresses the intake leaves by (the payment page, the sign-in) and comes back with the photos,
 * so the one press that left is the last one asked of the buyer.
 */
export const MUSE_NAME_PARAM = 'muse_name'

/** `address` carrying her name; the address itself when she has none yet. */
export function nameReturnAddress(address: string, name: string): string {
  const trimmed = name.trim()
  return trimmed ? withParam(address, MUSE_NAME_PARAM, trimmed) : address
}

/** The name an address brings back, bounded as the name field bounds it. */
export function readNameReturn(search: string): string {
  return (new URLSearchParams(search).get(MUSE_NAME_PARAM) ?? '').trim().slice(0, OWN_MUSE_NAME_MAX)
}
